import React, { useState, useEffect } from 'react';

// Persist the 'seen' state across SPA navigation, but reset on full page reload.
let hasShownLoaderThisSession = false;

export default function TheaterLoader({ onLoadingComplete }) {
  const [stage, setStage] = useState(hasShownLoaderThisSession ? 'done' : 'loading');

  useEffect(() => {
    if (hasShownLoaderThisSession) {
      if (onLoadingComplete) onLoadingComplete();
      return;
    }
    
    hasShownLoaderThisSession = true;

    // Timeline based on requirements
    const timer1 = setTimeout(() => {
      setStage('opening');
    }, 2500);

    const timer2 = setTimeout(() => {
      setStage('fading');
    }, 3500);

    const timer3 = setTimeout(() => {
      setStage('done');
      if (onLoadingComplete) onLoadingComplete();
    }, 3800);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
    };
  }, [onLoadingComplete]);

  if (stage === 'done') return null;

  // Realistic deep red velvet folds
  const curtainFolds = {
    backgroundColor: '#4a0000',
    backgroundImage: `
      linear-gradient(to bottom, rgba(0,0,0,0.8) 0%, transparent 10%, transparent 90%, rgba(0,0,0,0.8) 100%),
      repeating-linear-gradient(
        to right,
        #1a0000 0%,
        #4a0000 4%,
        #990000 10%,
        #cc0000 15%,
        #e60000 20%,
        #cc0000 25%,
        #990000 30%,
        #4a0000 36%,
        #1a0000 40%
      )
    `,
    backgroundSize: '100% 100%, 25% 100%',
    boxShadow: 'inset 0 0 50px rgba(0,0,0,0.9)'
  };

  return (
    <div className={`fixed inset-0 z-[99999] flex items-center justify-center overflow-hidden bg-[#050505] pointer-events-none transition-opacity duration-300 ${stage === 'fading' ? 'opacity-0' : 'opacity-100'}`}>
      
      {/* Background Stage Floor */}
      <div className={`absolute bottom-0 w-full h-1/3 bg-gradient-to-t from-[#2a0808] to-transparent opacity-80 z-0 transition-opacity duration-1000 ${stage === 'opening' || stage === 'fading' ? 'opacity-100' : 'opacity-0'}`}></div>

      {/* Realistic Spotlights */}
      <div className={`absolute top-[-10%] left-1/2 -translate-x-1/2 w-full h-[120%] flex justify-center gap-8 mix-blend-screen z-10 transition-opacity duration-1000 ${stage === 'opening' || stage === 'fading' ? 'opacity-100' : 'opacity-40'}`}>
        <div className="w-32 h-full bg-gradient-to-b from-white/40 via-white/10 to-transparent blur-xl origin-top -rotate-[15deg]"></div>
        <div className="w-40 h-full bg-gradient-to-b from-white/50 via-white/10 to-transparent blur-2xl origin-top"></div>
        <div className="w-32 h-full bg-gradient-to-b from-white/40 via-white/10 to-transparent blur-xl origin-top rotate-[15deg]"></div>
      </div>

      {/* Left Curtain */}
      <div 
        className={`absolute top-0 left-0 h-full w-1/2 border-r-[4px] border-[#800000] transition-transform duration-[1200ms] ease-[cubic-bezier(0.6,0.05,0.15,0.95)] z-30 shadow-[10px_0_30px_rgba(0,0,0,0.9)] ${stage === 'opening' || stage === 'fading' ? '-translate-x-full' : 'translate-x-0'}`}
        style={curtainFolds}
      >
        {/* Golden Fringe / Border */}
        <div className="absolute right-0 top-0 bottom-0 w-2 bg-[repeating-linear-gradient(to_bottom,#d4af37_0%,#aa7c11_2px,#d4af37_4px)] opacity-90 shadow-[-2px_0_5px_rgba(0,0,0,0.5)]"></div>
      </div>

      {/* Right Curtain */}
      <div 
        className={`absolute top-0 right-0 h-full w-1/2 border-l-[4px] border-[#800000] transition-transform duration-[1200ms] ease-[cubic-bezier(0.6,0.05,0.15,0.95)] z-30 shadow-[-10px_0_30px_rgba(0,0,0,0.9)] ${stage === 'opening' || stage === 'fading' ? 'translate-x-full' : 'translate-x-0'}`}
        style={curtainFolds}
      >
        {/* Golden Fringe / Border */}
        <div className="absolute left-0 top-0 bottom-0 w-2 bg-[repeating-linear-gradient(to_bottom,#d4af37_0%,#aa7c11_2px,#d4af37_4px)] opacity-90 shadow-[2px_0_5px_rgba(0,0,0,0.5)]"></div>
      </div>

      {/* Top Drape / Valance */}
      <div className="absolute top-0 left-0 w-full z-40 drop-shadow-[0_20px_25px_rgba(0,0,0,0.9)] flex h-24 md:h-32">
        {/* Repeating Scallops for the top valance */}
        {[...Array(8)].map((_, i) => (
          <div key={i} className="flex-1 h-full relative" style={{ overflow: 'visible' }}>
            <div 
              className="absolute top-[-20%] left-[-10%] w-[120%] h-[120%] rounded-b-full shadow-[0_10px_20px_rgba(0,0,0,0.8)]"
              style={{
                backgroundColor: '#6a0000',
                backgroundImage: `
                  radial-gradient(ellipse at center, transparent 40%, rgba(0,0,0,0.8) 100%),
                  linear-gradient(to bottom, #990000 0%, #4a0000 100%)
                `,
                borderBottom: '4px solid #aa7c11'
              }}
            >
              {/* Gold Fringe on Scallops */}
              <div className="absolute bottom-0 left-0 w-full h-1.5 bg-[repeating-linear-gradient(to_right,#d4af37_0%,#aa7c11_2px,#d4af37_4px)] rounded-b-full opacity-80"></div>
            </div>
          </div>
        ))}
      </div>

      {/* Center Content */}
      <div className={`absolute z-20 flex flex-col items-center justify-center transition-all duration-[800ms] ${stage === 'opening' || stage === 'fading' ? 'opacity-0 scale-110' : 'opacity-100 scale-100'}`}>
        
        {/* Center Glow */}
        <div className="absolute w-[400px] h-[400px] bg-[#ff3333]/10 blur-[80px] rounded-full animate-pulse mix-blend-screen"></div>
        
        {/* Logo & Text */}
        <div className="relative flex flex-col items-center animate-[fadeInUp_0.8s_ease-out_forwards]">
          <h1 className="text-5xl md:text-7xl font-extrabold text-transparent bg-clip-text bg-gradient-to-br from-[#fff6d5] via-[#d4af37] to-[#aa7c11] drop-shadow-[0_5px_15px_rgba(0,0,0,0.9)] tracking-tight mb-2">
            Lokanatya
          </h1>
          
          <p className="mt-4 text-[#e8d08c] font-serif text-lg md:text-2xl font-semibold tracking-[0.15em] text-center px-6 drop-shadow-[0_3px_5px_rgba(0,0,0,1)] uppercase">
            Welcome to Odisha's Biggest Jatra Platform
          </p>

          {/* Elegant Loading Dots */}
          <div className="mt-12 flex gap-3">
            {[0, 1, 2].map((i) => (
              <div 
                key={i} 
                className="w-3.5 h-3.5 bg-gradient-to-b from-[#ffd700] to-[#aa7c11] rounded-full shadow-[0_0_15px_rgba(212,175,55,0.8)]"
                style={{ 
                  animation: `bounce 1.4s infinite ease-in-out both`,
                  animationDelay: `${i * 0.16}s` 
                }}
              ></div>
            ))}
          </div>
        </div>
      </div>

      <style dangerouslySetInnerHTML={{ __html: `
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(30px) scale(0.95); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes bounce {
          0%, 80%, 100% { transform: scale(0); }
          40% { transform: scale(1); }
        }
      `}} />
    </div>
  );
}
